/**
 * The fallback projector for tool results with no explicit case (18.08.2026).
 *
 * `condenseToolResultForApi` decides what leaves the machine. Its default was
 * `JSON.stringify(result)` — send everything — which made minimisation opt-in:
 * the safe path required remembering to add a `case`, and forgetting was
 * silent. Every data-exposure finding in the 17.08 audit was one instance of
 * that single design choice.
 *
 * Inverting it is worth more than fixing the instances, because it changes the
 * failure mode. A tool added tomorrow with no case now leaks nothing beyond
 * identifiers and counts; the cost of forgetting is a less capable model, not
 * a privacy incident.
 *
 * Free-text VALUES are dropped rather than truncated. A truncated document is
 * still the document; the point is that an unreviewed tool cannot ship user
 * content at all. Any tool that genuinely needs to return content gets an
 * explicit case, which is a decision someone makes on purpose.
 *
 * The one deliberate exception is naming: `name`/`title`/`label` survive, capped
 * short, because an assistant that cannot see what a thing is called cannot act
 * on it. That is a documented cost, not an oversight — and it is why `message`
 * and `note` are NOT on the list, having previously let 200 characters of prose
 * through under a header claiming free text was dropped.
 */

/** Keys that carry identity or shape — safe, and what the model needs to act. */
const SAFE_KEYS = new Set([
  'id', 'ids', 'name', 'names', 'title', 'label', 'type', 'kind', 'status',
  'success', 'ok', 'error', 'exit_code', 'count', 'total', 'row_count',
  'columns', 'created', 'updated', 'deleted', 'moved_count', 'failed_count',
  'passed_count', 'skipped', 'truncated',
]);

/** Object nesting the projector will walk before giving up. */
const MAX_DEPTH = 2;

/**
 * Identity-ish keys keep a SHORT value; everything else in SAFE_KEYS is a flag
 * or a count. `name` and `title` have to survive — an assistant that cannot see
 * a collection's name is useless — but they are still user-authored strings, so
 * they are capped far below the general scalar limit. `message` and `note` were
 * here and are not any more: they name free prose by definition, and a tool
 * returning `{ message }` was shipping 200 characters of it through the net
 * that exists to stop exactly that.
 */
const NAMEY_KEYS = new Set(['name', 'names', 'title', 'label']);
const NAMEY_MAX = 80;

/** Any *_id / *_ids / *_count / *_name key is an identifier or a tally. */
const SAFE_SUFFIX = /(_id|_ids|_count|_name|_names|_at|_ms)$/;

const isSafeKey = (key) => SAFE_KEYS.has(key) || NAMEY_KEYS.has(key) || SAFE_SUFFIX.test(key);

/** Per-key value ceiling: a name is a label, not a place to put a paragraph. */
const fitsForKey = (key, value) =>
  typeof value === 'string' && NAMEY_KEYS.has(key) ? value.length <= NAMEY_MAX : true;

/** Values small enough to be structural rather than content. */
const isScalar = (v) =>
  v === null || ['boolean', 'number'].includes(typeof v) ||
  (typeof v === 'string' && v.length <= 200);

const MAX_ITEMS = 50;

/**
 * Project an arbitrary tool result down to identifiers, counts and flags.
 *
 * Recurses one level into arrays and objects so a list of created records
 * still reports what was created, without carrying any record's contents.
 */
export function projectUnlistedResult(result, depth = 0) {
  if (result === null || result === undefined) return result;
  if (Array.isArray(result)) {
    // The depth cap has to apply here too, or only object nesting is bounded
    // and an array-of-arrays recurses to whatever depth the data happens to be.
    if (depth >= MAX_DEPTH) return undefined;
    const items = result.slice(0, MAX_ITEMS).map((v) => projectUnlistedResult(v, depth + 1));
    return result.length > MAX_ITEMS
      ? { items, omitted: result.length - MAX_ITEMS }
      : items;
  }
  if (typeof result !== 'object') return isScalar(result) ? result : undefined;

  const out = {};
  let dropped = 0;
  for (const [key, value] of Object.entries(result)) {
    if (isSafeKey(key) && isScalar(value) && fitsForKey(key, value)) { out[key] = value; continue; }
    // Nested structures are worth one level of recursion — a `board` or
    // `request` wrapper is shape, not content.
    if (depth < MAX_DEPTH && value && typeof value === 'object') {
      const inner = projectUnlistedResult(value, depth + 1);
      const empty = inner === undefined
        || (Array.isArray(inner) ? inner.length === 0 : Object.keys(inner || {}).length === 0);
      if (!empty) { out[key] = inner; continue; }
    }
    if (value !== undefined) dropped += 1;
  }

  // Tell the model its view was reduced, so it asks rather than assuming the
  // field was absent.
  if (dropped > 0) out.fields_withheld = dropped;
  return out;
}
